/**
 * What a notification card renders and where it goes when clicked — decided once, in one pure
 * function, for both surfaces that list alerts (the homescreen strip and `/notifications`).
 *
 * The two kinds share a component (SPEC `New Post Watching` § In-app card: "`new_post` renders the
 * viral card component with a green header"), so the branch that used to be two components is now
 * data: a plan naming the strip's colour and text, the body to render, and the click target. Keeping
 * it out of the component is what makes it testable — the web suites run in `node` with no DOM.
 *
 * Every string here is either the server's, rendered verbatim (§12.5), or a fixed label; nothing is
 * computed from a number.
 */

import {
  watchDeepLinkPath,
  type FeedCardDTO,
  type NewPostNotificationDTO,
  type NotificationDTO,
  type ViralNotificationDTO,
} from '@lw/shared';

/**
 * The homescreen strip's heading. It carries both kinds, so it names neither — the cards' own
 * strips say which is which (SPEC § In-app card: retitled "New").
 */
export const STRIP_TITLE = 'New';

/** `.notif-strip` modifier that turns the purple alert bar green (`notifications.css`). */
export const NEW_POST_STRIP_CLASS = 'notif-strip--new';

/**
 * Where a plain click on the card body goes.
 *
 * - `preview` — the shared post popup, on the route the card is already on (viral).
 * - `route` — an in-app navigation to another screen: `/watch?post=<id>`, the Posts-to-Comment feed
 *   with the popup open over it.
 * - `external` — the post on LinkedIn, in a new tab (the degraded card's only way out).
 * - `none` — nothing to open; the card is inert.
 */
export type NotificationClickTarget =
  | { type: 'preview' }
  | { type: 'route'; to: string }
  | { type: 'external'; url: string }
  | { type: 'none' };

/** The alert chrome every face wears: the strip's colour and its already-composed left-hand text. */
type PlanChrome = {
  /** Extra class on `.notif-strip`; `null` keeps the default purple. */
  accentClass: string | null;
  label: string;
  detail: string;
  /** What `.notif-strip__reason` renders — composed here, rendered verbatim. */
  bannerText: string;
};

export type ViralCardPlan = PlanChrome & {
  kind: 'viral';
  card: FeedCardDTO;
  click: { type: 'preview' };
};

export type NewPostRichPlan = PlanChrome & {
  kind: 'new-rich';
  card: FeedCardDTO;
  click: { type: 'route'; to: string };
  /**
   * "Show posts" — the author's LinkedIn profile, off the live card. Green faces only: visiting a
   * minutes-old permalink looks like surveillance, and the post is the profile's top item anyway.
   */
  showPostsUrl: string;
};

export type NewPostDegradedPlan = PlanChrome & {
  kind: 'new-degraded';
  /** The frozen payload — the whole of the degraded body. */
  newPost: NewPostNotificationDTO['newPost'];
  click: { type: 'external'; url: string } | { type: 'none' };
  /** The same target, off the frozen payload; `null` on a pre-v5 row drops the button. */
  showPostsUrl: string | null;
};

export type NotificationCardPlan = ViralCardPlan | NewPostRichPlan | NewPostDegradedPlan;

/** Label and detail share one trimming line, so a long reason gives way before the date does. */
function bannerText(label: string, detail: string): string {
  return detail.length > 0 ? `${label}: ${detail}` : label;
}

export function notificationCardModel(notification: NotificationDTO): NotificationCardPlan {
  if (notification.type === 'viral_post') {
    const label = notification.refire ? 'Viral again' : 'Viral';
    const detail = [notification.reasonDisplay, notification.growthDisplay]
      .filter((part): part is string => part !== null && part.length > 0)
      .join(' • ');
    return {
      kind: 'viral',
      accentClass: null,
      label,
      detail,
      bannerText: bannerText(label, detail),
      card: notification.card,
      click: { type: 'preview' },
    };
  }

  const label = 'New post';
  // Both halves arrive finished: the potential frozen at fire and spelled at the API edge, the age
  // rebuilt per read there. The client only joins them — no arithmetic, no formatting.
  const detail = [notification.newPost.potentialDisplay, notification.newPost.postedAgoDisplay]
    .filter((part): part is string => part !== null && part.length > 0)
    .join(' • ');
  const chrome: PlanChrome = {
    accentClass: NEW_POST_STRIP_CLASS,
    label,
    detail,
    bannerText: bannerText(label, detail),
  };

  // A resolvable post means a live card, chips and all. The pair moves together (the card is built
  // from the id), but both are checked: a card without an id would have nowhere to click through to.
  if (notification.card !== null && notification.postId !== null) {
    return {
      ...chrome,
      kind: 'new-rich',
      card: notification.card,
      click: { type: 'route', to: watchDeepLinkPath(notification.postId) },
      showPostsUrl: notification.card.author.url,
    };
  }

  const url = notification.newPost.url;
  return {
    ...chrome,
    kind: 'new-degraded',
    newPost: notification.newPost,
    click: url === null ? { type: 'none' } : { type: 'external', url },
    showPostsUrl: notification.newPost.authorProfileUrl,
  };
}

/**
 * The list the preview popup's prev/next walks — **viral cards only**.
 *
 * A green card leaves the current route for `/watch`, so putting its post in this list would make
 * the walk jump screens mid-stride.
 */
export function notificationPreviewIds(notifications: NotificationDTO[]): number[] {
  return notifications
    .filter(
      (notification): notification is ViralNotificationDTO => notification.type === 'viral_post',
    )
    .map((notification) => notification.card.id);
}
